import {ViLanguageConfig} from '@frontend/locales/languages/ViLanguageConfig'
import {VnRegionConfig} from '@frontend/locales/regions/VnRegionConfig'
import {LocaleService as BaseLocaleService} from 'swayok-react-mdb-ui-kit/services/LocaleService'
import type {FrontendTranslations as Translations} from '../locales/en'
import {EnLanguageConfig} from '../locales/languages/EnLanguageConfig'
import {DemoRegionConfig} from '../locales/regions/DemoRegionConfig'
import type {
    LanguageCode,
    LanguageConfig,
    RegionCode,
    RegionConfig,
} from '../types/Locales.ts'

const isDevMode: boolean = process.env.NODE_ENV === 'development'

// Структура файлов локализации.
export type {Translations}

// Список известных языков и их настроек.
const knownLanguages: Record<LanguageCode, LanguageConfig> = {
    vi: Object.assign(ViLanguageConfig, {
        // Задаем loader именно тут, чтобы иметь возможность авто обновления через HMR (см. внизу).
        async loader() {
            await ViLanguageConfig.dateTime.loader()
            return (await import('../locales/vi')).default
        },
    }),
    en: Object.assign(EnLanguageConfig, {
        // Задаем loader именно тут, чтобы иметь возможность авто обновления через HMR (см. внизу).
        async loader() {
            await EnLanguageConfig.dateTime.loader()
            return isDevMode ? (await import('../locales/dev')).default : (await import('../locales/en')).default
        },
    }),
}
// Список известных регионов и их настроек.
const knownRegions: Record<RegionCode, RegionConfig> = {
    vn: VnRegionConfig,
    demo: isDevMode ? DemoRegionConfig : VnRegionConfig,
}

const allowEnLanguageByDefault = (
    isDevMode
    // || window.localeConfig?.language === 'en'
    // || window.localeConfig?.region === 'en'
)

// Список кодов поддерживаемых языков.
const allowedLanguageCodes: LanguageCode[] = (
    window.localeConfig?.languages
    ?? ['vi', ...(allowEnLanguageByDefault ? ['en'] : [])]
) as LanguageCode[]

// Список кодов поддерживаемых регионов.
const allowedRegionCodes: RegionCode[] = (
    window.localeConfig?.regions
    ?? ['vn']
) as RegionCode[]

// Список разрешенных локалей и их настроек.
const languages: Partial<Record<LanguageCode, LanguageConfig>> = {}
for (const language of allowedLanguageCodes) {
    languages[language] = knownLanguages[language]
}

// Список разрешенных регионов и их настроек.
const regions: Partial<Record<RegionCode, RegionConfig>> = {}
for (const region of allowedRegionCodes) {
    regions[region] = knownRegions[region]
}

// Язык по умолчанию.
const defaultLanguage: LanguageConfig = knownLanguages[allowedLanguageCodes[0]]
// Регион по умолчанию.
const defaultRegion: RegionConfig = knownRegions[allowedRegionCodes[0]]

// Сервис для работы с языками и регионами приложения.
export const LocaleService = new BaseLocaleService<
    LanguageCode,
    RegionCode,
    LanguageConfig,
    Translations,
    RegionConfig
>(
    languages,
    defaultLanguage,
    regions,
    defaultRegion
)

// Обновление локализации в dev среде с hot module replacement.
if (import.meta.webpackHot) {
    // Список файлов нужно указывать явно и точно так же как указано в locales.*.loader (см. наверху).
    import.meta.webpackHot.accept(['../locales/en', '../locales/vi', '../locales/dev'], (updatedFiles: string[]) => {
        // Обрабатываем все обновленные файлы локализаций и про необходимости перезагружаем их.
        console.log('[LocaleService] Language files changed', updatedFiles)
        LocaleService.languagesManager.handleHotModuleReload(updatedFiles)
    })
}
